import { Button, InputGroup, color } from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useDispatch } from "react-redux";
import { NAME_REGEX } from "@funded-here-interface/common";
import { useStyle } from "./RepresentativeDetails.styles";
import { Checkbox, MantineProvider } from "@mantine/core";
import AuthRepInfo from "../../components/AuthRepInfo/AuthRepInfo";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RepresentativeDetails: FC = () => {
  const { classes } = useStyle();
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const { userFirstName, userLastName, token } = useSelector(
    (state: RootState) => state.auth
  );
  const userId = useSelector((state: RootState) => state.auth.userId);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [repFirstName, setRepFirstName] = useState("");
  const [repLastName, setRepLastName] = useState("");
  const [representative_title, setRepresentativeTitle] = useState("");
  const [initPage, setInitPage] = useState(true);

  const [checked, setChecked] = useState(false);

  const [error, setError] = useState({
    repFirstName: "",
    repLastName: "",
    repTitle: "",
  });

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };
      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  const handleBack = () => {
    saveDraft(false);
    Navigate("/biz-profile");
  };

  const handleNext = () => {
    saveDraft(false);
    const customErr = { ...error };
    let valid = true;

    if (!repFirstName) {
      valid = false;
      customErr.repFirstName = "Representative's First Name is required";
    }
    if (!repLastName) {
      valid = false;
      customErr.repLastName = "Representative's Last Name is required";
    }
    if (!representative_title) {
      valid = false;
      customErr.repTitle = "Representative's Title is required";
    }

    if (valid) {
      Navigate("/bank-details");
    } else {
      setError(customErr);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await fetchDraft(parseInt(userId));

          if (data) {
            setRepFirstName(data.repFirstName);
            setRepLastName(data.repLastName);
            setRepresentativeTitle(data.repTitle);

            if (data.authChecked) {
              setChecked(data.authChecked);
            }
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId, dispatch]);

  useEffect(() => {
    if (checked) {
      setRepFirstName(userFirstName as string);
      setRepLastName(userLastName as string);
    } else {
      setRepFirstName(repFirstName || "");
      setRepLastName(repLastName || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, repFirstName, repLastName]);

  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        repFirstName: repFirstName,
        repLastName: repLastName,
        repTitle: representative_title,
        authChecked: checked,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <OnboardingLayout
      step={2}
      showSteps={true}
      content={
        <div className={classes.container}>
          <AuthRepInfo />
          <div className={classes.inputBox}>
            <MantineProvider theme={{ cursorType: "pointer" }}>
              <Checkbox
                style={{ marginBottom: "20px" }}
                checked={checked}
                label="I'm the authorized representative"
                onChange={(event) => setChecked(event.currentTarget.checked)}
              />
            </MantineProvider>

            {!checked && (
              <InputGroup
                isErrored={error.repFirstName}
                onChange={(value: string) => {
                  error.repFirstName = "";
                  setRepFirstName(value.replace(NAME_REGEX, ""));
                }}
                labelText="First Name- Authorized Representative"
                id="firstName"
                value={repFirstName}
              />
            )}
            {!checked && (
              <InputGroup
                isErrored={error.repLastName}
                onChange={(value: any) => {
                  error.repLastName = "";
                  setRepLastName(value.replace(NAME_REGEX, ""));
                }}
                labelText="Last Name- Authorized Representative"
                id="lastName"
                value={repLastName}
              />
            )}
            <InputGroup
              isErrored={error.repTitle}
              onChange={(value: string) => {
                error.repTitle = "";
                setRepresentativeTitle(value.replace(NAME_REGEX, ""));
              }}
              labelText="Title of Representative"
              id="representative_title"
              value={representative_title}
            />
            <div className={classes.buttonDiv}>
              <div className={classes.buttonWrapper}>
                <Button
                  onClick={handleBack}
                  backgroundColor={color.WHITE}
                  textColor={color.FHGREEN}
                  border="1px solid #338F86"
                  width="100%"
                >
                  Back
                </Button>
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  onClick={handleNext}
                  textColor={color.WHITE}
                  backgroundColor={color.FHGREEN}
                  width="100%"
                >
                  Next
                </Button>
              </div>
            </div>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={() => saveDraft()}
                textColor={color.WHITE}
                backgroundColor={color.LOANTEXT}
                width="100%"
              >
                Save Draft
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default RepresentativeDetails;
